import React, { useCallback, useEffect, useRef, useState } from "react"
import Client from "../../models/client"
import useSWR from "swr"
import { createEmailNotice, fetchEmailNotice, updateEmailNotice } from "../api"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { BaseModal, requiredMark, requireLabelStyles } from "./BaseModal"
import { Box, Button, Group, Loader, NumberInput, Radio, ScrollArea, Select, Stack, Text, Textarea, TextInput } from "@mantine/core"
import ComingCouponEmailContentEditor from "./ComingCouponEmailContentEditor"
import { confirmDiscardChanges } from "../utils/confirm"

const URL_REGEX =
  /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/

interface Props {
  opened: boolean
  onClose: () => void
  client: Client
  noticeId: number | null
  eventOptions: { id: number; name: string; record_type: string }[]
  onSaved?: () => void
}

interface FormValues {
  enduser_event_id: string
  subject: string
  upper_limit: number | string
  kv_img_url: string
  cta_url: string
  content: string
  custom_css: string
  is_active: string
  to_type: string
}

const defaultValues: FormValues = {
  enduser_event_id: '',
  subject: '',
  upper_limit: 0,
  kv_img_url: '',
  cta_url: '',
  content: '',
  custom_css: '',
  is_active: 'true',
  to_type: 'inviter',
}

const ComingCouponEmailNoticeFormModal: React.FC<Props> = ({
  opened,
  onClose,
  client,
  noticeId,
  eventOptions,
  onSaved,
}) => {
  const isNew = noticeId === null
  const [submitting, setSubmitting] = useState(false)

  const { data, isLoading } = useSWR(
    opened && !isNew ? `/admin/clients/${client.id}/email_notices/${noticeId}` : null,
    () => fetchEmailNotice(client.id, noticeId as number),
  )

  const form = useForm<FormValues>({
    initialValues: defaultValues,
    validate: {
      enduser_event_id: (v) => (!v ? '必須です' : null),
      subject: (v) =>
        !v ? '件名は必須です' : v.length > 255 ? '255文字以内で入力してください' : null,
      content: (v) => {
        const text = (v ?? '').replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim()
        const hasMedia = /<img\b/i.test(v ?? '')
        return !text && !hasMedia ? '通知内容は必須です' : null
      },
      kv_img_url: (v) =>
        !v ? '画像URLは必須です' : !URL_REGEX.test(v) ? 'URLの形式が正しくありません' : null,
      upper_limit: (v) => {
        if (v === '' || v === null || v === undefined) return '必須です'
        if (isNaN(Number(v))) return '数字で入力してください'
        if (Number(v) < 0) return 'マイナスの数字は入力できません'
        return null
      },
    },
  })

  const formRef = useRef(form)
  formRef.current = form
  const handleContentChange = useCallback((value: string) => {
    formRef.current.setFieldValue('content', value)
    formRef.current.clearFieldError('content')
  }, [])

  useEffect(() => {
    if (!opened) return
    if (isNew) {
      form.setValues(defaultValues)
      form.resetDirty()
      return
    }
    if (data) {
      form.setValues({
        enduser_event_id: String(data.enduser_event_id ?? ''),
        subject: data.subject ?? '',
        upper_limit: data.upper_limit ?? 0,
        is_active: String(data.is_active),
        to_type: data.to_type ?? 'inviter',
        content: data.body_json?.content ?? '',
        cta_url: data.body_json?.cta_url ?? '',
        kv_img_url: data.body_json?.kv_img_url ?? '',
        custom_css: data.body_json?.custom_css ?? '',
      })
      form.resetDirty()
    }
  }, [opened, data, isNew])

  const handleClose = () => {
    if (
      form.isDirty() &&
      !confirmDiscardChanges()
    ) {
      return
    }
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)
    try {
      if (isNew) {
        await createEmailNotice(
          client.id,
          Number(values.enduser_event_id),
          values.to_type,
          values.subject,
          values.content,
          values.cta_url,
          values.kv_img_url,
          values.is_active === 'true',
          Number(values.upper_limit),
          values.custom_css,
        )
        notifications.show({ message: 'メール通知を作成しました', color: 'teal' })
      } else {
        await updateEmailNotice(
          client.id,
          noticeId as number,
          Number(values.enduser_event_id),
          values.to_type,
          values.subject,
          values.content,
          values.cta_url,
          values.kv_img_url,
          values.is_active === 'true',
          Number(values.upper_limit),
          values.custom_css,
        )
        notifications.show({ message: 'メール通知を更新しました', color: 'teal' })
      }
      onSaved?.()
      onClose()
    } catch (e: any) {
      console.error('メール通知の保存に失敗しました', e.response?.status, e.response?.data)
      const data = e.response?.data
      const msg = data?.errors?.join('\n') ?? data?.error ?? '保存に失敗しました'
      notifications.show({ message: msg, color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  const showLoader = !isNew && (isLoading || !data)

  const eventSelectData = (eventOptions ?? []).map((o) => ({
    value: String(o.id),
    label: o.name,
  }))

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title={isNew ? 'メール通知作成' : 'メール通知編集'}
      width={1000}
      size="85vh"
      onExited={() => {
        form.setValues(defaultValues)
        form.resetDirty()
      }}
    >
      {showLoader ? (
        <Group justify="center" py="xl" style={{ width: '100%' }}>
          <Loader />
        </Group>
      ) : (
        <form
          onSubmit={form.onSubmit(handleSubmit)}
          style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
        >
          <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
            <Stack gap="md" p="md">
              <Select
                label={<><span>イベント</span>{requiredMark}</>}
                placeholder="未設定"
                data={eventSelectData}
                allowDeselect={false}
                styles={requireLabelStyles}
                {...form.getInputProps('enduser_event_id')}
              />

              <Select
                label="宛先"
                data={[
                  { value: 'inviter', label: '紹介者' },
                  { value: 'guest', label: 'ゲスト' },
                ]}
                allowDeselect={false}
                {...form.getInputProps('to_type')}
              />

              <TextInput
                label={<><span>件名</span>{requiredMark}</>}
                styles={requireLabelStyles}
                {...form.getInputProps('subject')}
              />

              <NumberInput
                label="送信上限数（0は無制限）"
                min={0}
                allowNegative={false}
                {...form.getInputProps('upper_limit')}
              />

              <TextInput
                label={<><span>KV画像URL</span>{requiredMark}</>}
                styles={requireLabelStyles}
                {...form.getInputProps('kv_img_url')}
              />

              <Box>
                <Text size="sm" fw={500} mb={8} style={requireLabelStyles}>
                  通知内容{requiredMark}
                </Text>
                <Box bg="#fff" p="sm" style={{ borderRadius: 8, border: '1px solid #0000001A' }}>
                  <ComingCouponEmailContentEditor
                    key={isNew ? 'new' : String(noticeId)}
                    initialValue={isNew ? '' : data?.body_json?.content ?? ''}
                    client={client}
                    onChange={handleContentChange}
                    toType={form.values.to_type}
                  />
                </Box>
                {form.errors.content && (
                  <Text c="red" size="sm" mt={4}>{form.errors.content}</Text>
                )}
              </Box>

              <Textarea
                label="カスタムCSS"
                autosize
                minRows={10}
                {...form.getInputProps('custom_css')}
              />

              <Radio.Group label="ステータス" {...form.getInputProps('is_active')}>
                <Group mt="xs">
                  <Radio value="true" label="有効" />
                  <Radio value="false" label="無効" />
                </Group>
              </Radio.Group>
            </Stack>
          </ScrollArea>

          <Box p="md" style={{ borderTop: '1px solid #0000001A', backgroundColor: '#fff', flexShrink: 0 }}>
            <Group justify="flex-end" gap={8}>
              <Button variant="outline" radius="xl" color="gray" onClick={handleClose} disabled={submitting}>
                キャンセル
              </Button>
              <Button variant="filled" radius="xl" type="submit" loading={submitting}>
                保存
              </Button>
            </Group>
          </Box>
        </form>
      )}
    </BaseModal>
  )
}

export default ComingCouponEmailNoticeFormModal
